import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, of, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Service de détection de l'état réseau.
 *
 * <p>{@code navigator.onLine} est notoirement peu fiable au Niger : il
 * retourne souvent {@code true} alors que la 4G est saturée et qu'aucun
 * paquet ne passe. On combine donc trois signaux :</p>
 * <ol>
 *   <li>Les events natifs {@code online} / {@code offline} du navigateur</li>
 *   <li>Un ping HTTP périodique vers un endpoint backend léger</li>
 *   <li>Un signal manuel ({@code markOffline} / {@code markOnline}) déclenché
 *       par l'intercepteur HTTP quand une requête timeout ou échoue avec une
 *       erreur réseau.</li>
 * </ol>
 *
 * <p>Expose un {@link BehaviorSubject} {@code online$} consommable partout
 * dans l'app, et un {@code latencyMs$} pour afficher la qualité du lien.</p>
 */
@Injectable({ providedIn: 'root' })
export class NetworkService {
  private http = inject(HttpClient);

  /** True si la dernière vérification a réussi. */
  private readonly _online$ = new BehaviorSubject<boolean>(this.initialOnline());
  readonly online$ = this._online$.asObservable();

  /** Latence du dernier ping en millisecondes, null si offline. */
  private readonly _latencyMs$ = new BehaviorSubject<number | null>(null);
  readonly latencyMs$ = this._latencyMs$.asObservable();

  /** Timestamp du dernier passage à online. */
  private lastOnlineAt: Date | null = this.initialOnline() ? new Date() : null;
  /** Timestamp du dernier passage à offline. */
  private lastOfflineAt: Date | null = null;

  /** Intervalle de ping en ms (15s — assez agressif pour réseaux instables Niamey). */
  private readonly PING_INTERVAL_MS = 15_000;
  /** Timeout d'un ping (4s — au-delà on considère qu'on est offline). */
  private readonly PING_TIMEOUT_MS = 4_000;

  private pingTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.checkConnection(true));
      window.addEventListener('offline', () => this.setOnline(false));
      this.startPolling();
    }
  }

  /** Valeur instantanée — utile en dehors d'un subscribe. */
  isOnline(): boolean {
    return this._online$.value;
  }

  getLastOnlineAt(): Date | null {
    return this.lastOnlineAt;
  }

  getLastOfflineAt(): Date | null {
    return this.lastOfflineAt;
  }

  /** Force un check immédiat (au lieu d'attendre le prochain tick). */
  checkNow(): Observable<boolean> {
    return new Observable<boolean>((sub) => {
      this.checkConnection(true).then((ok) => {
        sub.next(ok);
        sub.complete();
      });
    });
  }

  /**
   * Marque l'app comme offline. Appelée par l'intercepteur HTTP quand une
   * requête échoue avec une erreur réseau (status 0 ou timeout).
   */
  markOffline(): void {
    this.setOnline(false);
  }

  /** Marque l'app comme online (utile après un replay réussi). */
  markOnline(latencyMs?: number): void {
    if (typeof latencyMs === 'number') this._latencyMs$.next(latencyMs);
    this.setOnline(true);
  }

  // ─── interne ──────────────────────────────────────────────────────────

  private initialOnline(): boolean {
    return typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
  }

  /**
   * True si on est sur une page d'authentification (login, register, etc.),
   * toutes regroupées sous la route /authentication. Sur ces pages on
   * n'exécute pas le ping de connectivité.
   */
  private isAuthPage(): boolean {
    return typeof window !== 'undefined'
      && window.location.pathname.includes('/authentication');
  }

  private startPolling(): void {
    if (this.pingTimer) return;
    this.pingTimer = setInterval(() => {
      this.checkConnection(false);
    }, this.PING_INTERVAL_MS);
    // Ping initial après 2s pour ne pas geler le boot.
    setTimeout(() => this.checkConnection(false), 2_000);
  }

  /**
   * Effectue un ping HTTP léger et met à jour {@link _online$}.
   *
   * @param verbose si true, un échec passe immédiatement en offline (forcé)
   */
  private async checkConnection(verbose: boolean): Promise<boolean> {
    // Pas de ping sur les pages non authentifiées (login, register, mot de passe
    // oublié, 2FA…). Inutile avant connexion et évite tout trafic /health/ping
    // sur ces écrans. Le timer continue de tourner mais ne fait rien ici.
    if (this.isAuthPage()) {
      return this._online$.value;
    }
    const start = performance.now();
    const url = `${environment.apiUrl}/health/ping`;
    return new Promise<boolean>((resolve) => {
      this.http
        .get(url, {
          responseType: 'text',
          // X-Offline-Skip : bypass de l'offlineInterceptor (sinon boucle de cache)
          // X-Skip-Error-Toast : pas de notification utilisateur (sinon spam de toasts
          //   toutes les 15s quand l'atelier est hors ligne)
          headers: {
            'X-Offline-Skip': '1',
            'X-Skip-Error-Toast': 'true',
          },
        })
        .pipe(
          timeout(this.PING_TIMEOUT_MS),
          catchError(() => of(null)),
        )
        .subscribe((res) => {
          const elapsed = Math.round(performance.now() - start);
          if (res === null) {
            this._latencyMs$.next(null);
            if (verbose || this._online$.value) {
              this.setOnline(false);
            }
            resolve(false);
          } else {
            this._latencyMs$.next(elapsed);
            this.setOnline(true);
            resolve(true);
          }
        });
    });
  }

  private setOnline(online: boolean): void {
    if (this._online$.value === online) return;
    if (online) {
      this.lastOnlineAt = new Date();
    } else {
      this.lastOfflineAt = new Date();
      this._latencyMs$.next(null);
    }
    this._online$.next(online);
  }
}
